import { useEffect } from "react";
import { useLanguage } from "../context/LanguageContext";
import { translatePath } from "../utils/routePaths";

const SITE_NAME = "DiscoverSvaneti";
const BASE_URL = "https://discoversvaneti.com";
const DEFAULT_DESCRIPTION = {
  en: "Discover Svaneti, Georgia — medieval mountain towers, glaciers, hiking and skiing routes, local culture and cuisine. Your bilingual guide to one of the Caucasus's most breathtaking regions.",
  ka: "აღმოაჩინეთ სვანეთი, საქართველო — შუა საუკუნეების კოშკები, მყინვარები, სალაშქრო და სათხილამურო მარშრუტები, ადგილობრივი კულტურა და სამზარეულო. თქვენი ორენოვანი გზამკვლევი კავკასიის ერთ-ერთ ულამაზეს რეგიონში.",
};
const DEFAULT_TITLE = {
  en: `${SITE_NAME} — Explore Svaneti, Georgia`,
  ka: `${SITE_NAME} — გაეცანით სვანეთს, საქართველო`,
};

function setMeta(attr, name, content) {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function clearManagedLinks() {
  document.querySelectorAll('link[data-seo-managed="true"]').forEach((el) => el.remove());
}

function addLink(rel, href, hreflang) {
  const el = document.createElement("link");
  el.setAttribute("rel", rel);
  el.setAttribute("href", href);
  if (hreflang) el.setAttribute("hreflang", hreflang);
  el.setAttribute("data-seo-managed", "true");
  document.head.appendChild(el);
}

function clearManagedJsonLd() {
  document.querySelectorAll('script[data-seo-managed-jsonld="true"]').forEach((el) => el.remove());
}

function addJsonLd(data) {
  const el = document.createElement("script");
  el.type = "application/ld+json";
  el.setAttribute("data-seo-managed-jsonld", "true");
  el.textContent = JSON.stringify(data);
  document.head.appendChild(el);
}

// path must be the CURRENT-language path with its /:lang prefix, e.g.
// sectionPath(language, "place", id) -> "/en/place/xyz" or "/ka/ლოკაციები/xyz".
// jsonLd is optional: a single structured-data object or an array of them.
// noSuffix skips appending " | SITE_NAME" when the caller is passing an
// already-complete, exact title/ogTitle string (e.g. the homepage).
// ogTitle/ogDescription let social previews use different copy than the
// <title>/meta description; both fall back to title/description.
export function useSEO({ title, description, ogTitle, ogDescription, image, path, jsonLd, noSuffix } = {}) {
  const { language } = useLanguage();

  useEffect(() => {
    const decorate = (t) => (noSuffix ? t : `${t} | ${SITE_NAME}`);
    const fullTitle = title ? decorate(title) : DEFAULT_TITLE[language];
    const desc = description || DEFAULT_DESCRIPTION[language];
    const socialTitle = ogTitle ? decorate(ogTitle) : fullTitle;
    const socialDesc = ogDescription || desc;
    const canonicalPath = path || `/${language}`;
    const url = `${BASE_URL}${canonicalPath}`;

    document.title = fullTitle;
    document.documentElement.lang = language;
    setMeta("name", "description", desc);
    setMeta("property", "og:title", socialTitle);
    setMeta("property", "og:description", socialDesc);
    setMeta("property", "og:url", url);
    setMeta("property", "og:type", "website");
    setMeta("property", "og:site_name", SITE_NAME);
    setMeta("property", "og:locale", language === "ka" ? "ka_GE" : "en_US");
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", socialTitle);
    setMeta("name", "twitter:description", socialDesc);
    if (image) {
      setMeta("property", "og:image", image);
      setMeta("name", "twitter:image", image);
    }

    // Canonical + hreflang alternates. Each language keeps its own canonical
    // URL (never cross-canonicalized to the other language) so both indexed
    // separately, while hreflang tells Google they're translations of each
    // other. x-default points at the English URL, matching the site's
    // browser-language auto-detect fallback.
    clearManagedLinks();
    addLink("canonical", url);
    const otherLang = language === "ka" ? "en" : "ka";
    const otherPath = translatePath(canonicalPath, otherLang);
    const enPath = language === "en" ? canonicalPath : otherPath;
    const kaPath = language === "ka" ? canonicalPath : otherPath;
    addLink("alternate", `${BASE_URL}${enPath}`, "en");
    addLink("alternate", `${BASE_URL}${kaPath}`, "ka");
    addLink("alternate", `${BASE_URL}${enPath}`, "x-default");

    clearManagedJsonLd();
    if (jsonLd) {
      (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).forEach(addJsonLd);
    }
  }, [title, description, ogTitle, ogDescription, image, path, jsonLd, noSuffix, language]);
}
